import type { ConsoleOrganization } from '@sentinel/shared';
import { Button, Panel, SelectField, TextAreaField } from '@sentinel/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const K = 'console.support.form';
const REASON_MIN = 10;

interface Props {
  organizations: readonly ConsoleOrganization[];
  busy: boolean;
  onSubmit: (input: { organizationId: string; reason: string }) => void;
}

/** A request for a support window: which organization, and why (the Admin reads it). */
export function RequestAccessForm({ organizations, busy, onSubmit }: Props) {
  const { t } = useTranslation();
  const [organizationId, setOrganizationId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const submit = () => {
    const tooShort = reason.trim().length < REASON_MIN;
    setError(tooShort ? t(`${K}.reasonError`, { min: REASON_MIN }) : undefined);
    if (!tooShort && organizationId !== '') onSubmit({ organizationId, reason: reason.trim() });
  };
  return (
    <Panel title={t(`${K}.title`)}>
      <div className="stack">
        <SelectField
          id="support-org"
          label={t(`${K}.organization`)}
          value={organizationId}
          onChange={(e) => setOrganizationId(e.target.value)}
          options={[
            { value: '', label: '—' },
            ...organizations.map((o) => ({ value: o.id, label: o.name })),
          ]}
        />
        <TextAreaField
          id="support-reason"
          label={t(`${K}.reason`)}
          hint={t(`${K}.reasonHint`)}
          rows={3}
          value={reason}
          error={error}
          onChange={(e) => setReason(e.target.value)}
        />
        <Button
          variant="primary"
          style={{ alignSelf: 'flex-start' }}
          disabled={busy || organizationId === ''}
          onClick={submit}
        >
          {t(`${K}.submit`)}
        </Button>
      </div>
    </Panel>
  );
}
